import type { Condition, ConditionalDataOutput, ConditionalStep } from '@/types/automationSteps.ts';
import { Divider, Group, Stack, Text } from '@mantine/core';
import { useMemo } from 'react';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import useIsNewStep from '@/hooks/useIsNewStep.ts';
import StepFieldsRemoveButton from '@/pages/Editor/components/StepFieldsRemoveButton.tsx';
import InputSourceSelectField from '@/pages/Editor/components/SharedFields/InputSourceSelectField.tsx';
import {
  ConditionAction,
  ExecutionType,
  isProvidedCodeSourceMode,
} from '@/types/automationFields.ts';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea.tsx';
import StyledSwitch from '@/components/StyledInputFields/StyledSwitch.tsx';
import ConditionActionSelectField from '@/pages/Editor/components/StepSidebar/StepForms/ConditionalStep/ConditionActionSelectField.tsx';
import ConditionDataOutputFields from '@/pages/Editor/components/StepSidebar/StepForms/ConditionalStep/ConditionDataOutputFields.tsx';
import MultiDataInputSelectorUI from '@/pages/Editor/components/SharedFields/DataInputSelector/MultiDataInputSelector/MultiDataInputSelectorUI.tsx';
import { ELSE_CLAUSE_ID, STEP_FIELD_MAX_LENGTH, STEP_LOGIC_MAX_LENGTH } from '@/utils/constants.ts';
import StyledInputLabel from '@/components/StyledInputFields/StyledInputLabel.tsx';
import {
  getMultiLogicDataInputs,
  inputSourceAcceptsDataInput,
  inputSourceAcceptsUserSpecified,
} from '@/pages/Editor/components/SharedFields/utils.ts';
import {
  getCodeVariableNameErrorMessage,
  hasCodeInputBindingIssues,
  shouldShowCodeInputBindingFields,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/codeInputBindings.ts';
import {
  getDecisionCodeSourceMode,
  getDecisionExecutionType,
} from '@/utils/decisionControlLogic.ts';
import DecisionControlLogicFields from '@/pages/Editor/components/StepSidebar/StepForms/DecisionControlLogicFields.tsx';
import { canMarkAllOutputsUnavailable } from '@/pages/Editor/components/StepSidebar/StepForms/ConditionalStep/conditionActionOptions.ts';

type props = {
  condition: Condition;
  onChangeNoDelay: (updatedCondition: Condition) => void;
  onChangeDelay: (updatedCondition: Condition) => void;
  onDelete: () => void;
  step: ConditionalStep;
  conditionIndex: number;
  dataOutputs: ConditionalDataOutput[];
};

const ConditionFields = ({
  condition,
  onChangeNoDelay,
  onChangeDelay,
  onDelete,
  step,
  conditionIndex,
  dataOutputs,
}: props) => {
  const dataInputError = useStepSpecificError(step, StepErrorType.InvalidDataInput, conditionIndex);

  const duplicateConditionName = useStepSpecificError(
    step,
    StepErrorType.DuplicateConditionName,
    conditionIndex,
  );

  const emptyLogicDescription = useStepSpecificError(
    step,
    StepErrorType.EmptyDescription,
    conditionIndex,
  );

  const invalidLogicDataInput = useStepSpecificError(
    step,
    StepErrorType.InvalidLogicDataInput,
    conditionIndex,
  );
  const invalidConditionAction = useStepSpecificError(
    step,
    StepErrorType.InvalidConditionAction,
    conditionIndex,
  );
  const invalidDisabledState = useStepSpecificError(
    step,
    StepErrorType.InvalidConditionalDisabledState,
    conditionIndex,
  );
  const invalidProvidedCode = useStepSpecificError(
    step,
    StepErrorType.InvalidProvidedCode,
    conditionIndex,
  );
  const codeInputBindingError = useStepSpecificError(
    step,
    StepErrorType.InvalidCodeInputBinding,
    conditionIndex,
  );

  const isNewStep = useIsNewStep(step.step_id);

  const hasDataOutputs =
    dataOutputs.length > 0 &&
    [ConditionAction.ChildSteps, ConditionAction.ReturnDataset].includes(condition.action);

  const conditionActionIsDataOutput = condition.action === ConditionAction.ReturnDataset;
  const canMarkOutputsUnavailable = canMarkAllOutputsUnavailable(dataOutputs);
  const unavailableOutputActionError =
    condition.action !== ConditionAction.Skip
      ? undefined
      : dataOutputs.length === 0
        ? 'Mark All Outputs Unavailable requires at least one declared conditional output.'
        : canMarkOutputsUnavailable
          ? undefined
          : 'Mark All Outputs Unavailable requires every declared output to allow runtime unavailability.';

  const isElseCondition = condition.condition_id === ELSE_CLAUSE_ID;
  const isDisabled = condition.is_disabled ?? false;
  const showDisabledReason = isDisabled || Boolean(condition.disabled_reason) || Boolean(invalidDisabledState);
  const requireCodeInputNames =
    !isElseCondition && getDecisionExecutionType(condition.control_logic) === ExecutionType.Code;
  const hasCodeInputBindingErrors = step.errors.some(
    (error) =>
      error.type === StepErrorType.InvalidCodeInputBinding && error.index === conditionIndex,
  );
  const hasVisibleBindingIssue =
    hasCodeInputBindingErrors || hasCodeInputBindingIssues(condition.condition_data_inputs);
  const codeInputNameErrorsByIndex = useMemo(() => {
    return Object.fromEntries(
      step.errors
        .filter(
          (error) =>
            error.type === StepErrorType.InvalidCodeInputBinding &&
            error.index === conditionIndex &&
            error.secondary_index !== null,
        )
        .map((error) => [
          error.secondary_index as number,
          getCodeVariableNameErrorMessage(error.message),
        ]),
    );
  }, [conditionIndex, step.errors]);

  return (
    <Stack
      style={{ borderColor: '#ECECED', borderWidth: 1, borderRadius: 5 }}
      bg={'#F6F6F6'}
      p={'xs'}
      gap={'xs'}
    >
      <Stack>
        <Group gap={'xs'} wrap={'nowrap'} align={'start'}>
          <StyledTextInput
            label="Name"
            value={condition.name}
            placeholder="Path Name"
            onChange={(event) => {
              const value = event.currentTarget.value.replace(/[/\n]/g, '');
              onChangeDelay({ ...condition, name: value });
            }}
            required={true}
            maxLength={STEP_FIELD_MAX_LENGTH}
            w={'100%'}
            error={!condition.name && !isNewStep ? 'Name cannot be empty' : duplicateConditionName}
          />
          {!isElseCondition && <StepFieldsRemoveButton onDelete={onDelete} />}
        </Group>
      </Stack>

      {!isElseCondition && (
        <>
          <StyledSwitch
            label="Disabled"
            description="Disabled branches remain configured but are skipped before their inputs and logic are evaluated."
            checked={isDisabled}
            onChange={(event) =>
              onChangeDelay({
                ...condition,
                is_disabled: event.currentTarget.checked,
                disabled_reason: event.currentTarget.checked ? (condition.disabled_reason ?? '') : '',
              })
            }
          />

          {showDisabledReason && (
            <StyledTextArea
              label="Disabled reason"
              value={condition.disabled_reason ?? ''}
              placeholder={
                isDisabled
                  ? 'Explain what must be addressed before this branch can be re-enabled.'
                  : 'Clear this stale reason or disable the branch.'
              }
              onChange={(event) =>
                onChangeDelay({
                  ...condition,
                  disabled_reason: event.currentTarget.value,
                })
              }
              required={isDisabled}
              autosize={true}
              minRows={2}
              error={invalidDisabledState}
              maxLength={STEP_LOGIC_MAX_LENGTH}
            />
          )}

          <InputSourceSelectField
            label="Description Source"
            value={condition.condition_logic_source}
            onChange={(value) => onChangeNoDelay({ ...condition, condition_logic_source: value })}
          />

          {inputSourceAcceptsUserSpecified(condition.condition_logic_source) && (
            <Stack>
              <StyledTextArea
                label="Condition Description"
                value={condition.condition_logic_description}
                placeholder="Describe when this rule should apply (for example: `The total amount is over $10,000`)"
                onChange={(event) => {
                  onChangeDelay({
                    ...condition,
                    condition_logic_description: event.currentTarget.value,
                  });
                }}
                required={true}
                autosize={true}
                minRows={2}
                error={emptyLogicDescription}
                maxLength={STEP_LOGIC_MAX_LENGTH}
              />
            </Stack>
          )}

          {inputSourceAcceptsDataInput(condition.condition_logic_source) && (
            <MultiDataInputSelectorUI
              stepId={step.step_id}
              label={'Condition Logic Data Inputs'}
              error={invalidLogicDataInput}
              dataInputs={getMultiLogicDataInputs(condition.condition_logic_data_inputs)}
              updateDataInputs={(dataInputs) =>
                onChangeNoDelay({
                  ...condition,
                  condition_logic_data_inputs: dataInputs,
                })
              }
            />
          )}

          <MultiDataInputSelectorUI
            dataInputs={condition.condition_data_inputs}
            stepId={step.step_id}
            updateDataInputs={(dataInputs) =>
              onChangeNoDelay({ ...condition, condition_data_inputs: dataInputs })
            }
            error={dataInputError || codeInputBindingError}
            label={'Data Inputs for Evaluating Condition'}
            requireCodeInputNames={requireCodeInputNames}
            showCodeInputNameFields={shouldShowCodeInputBindingFields({
              requireCodeInputNames,
              isProvidedCode: isProvidedCodeSourceMode(
                getDecisionCodeSourceMode(condition.control_logic),
              ),
              hasBindingIssues: hasVisibleBindingIssue,
            })}
            codeInputNameErrorsByIndex={codeInputNameErrorsByIndex}
          />

          <DecisionControlLogicFields
            controlLogic={condition.control_logic}
            dataInputs={condition.condition_data_inputs}
            invalidProvidedCode={invalidProvidedCode}
            onChange={(control_logic) =>
              onChangeDelay({
                ...condition,
                control_logic,
              })
            }
            onChangeNoDelay={(control_logic) =>
              onChangeNoDelay({
                ...condition,
                control_logic,
              })
            }
          />
        </>
      )}

      <ConditionActionSelectField
        label={isElseCondition ? 'Action' : 'Path Action'}
        value={condition.action}
        onChange={(action) => onChangeNoDelay({ ...condition, action })}
        error={
          invalidConditionAction || unavailableOutputActionError
        }
        canMarkOutputsUnavailable={canMarkOutputsUnavailable}
      />

      {(hasDataOutputs || conditionActionIsDataOutput) && (
        <>
          <Divider mt="xs" mb={'0'} />
          <span>
            <StyledInputLabel>Outputs</StyledInputLabel>
            <Stack
              style={{ borderColor: '#ECECED', borderWidth: 1, borderRadius: 5 }}
              bg={'#F6F6F6'}
              p={'xs'}
              pt={0}
              gap={'xs'}
            >
              {condition.data_outputs.map((output, dataOutputIndex) => (
                <ConditionDataOutputFields
                  key={output.output_id}
                  conditionAction={condition.action}
                  conditionDataOutput={output}
                  onChangeNoDelay={(updated) => {
                    const updatedOutputs = condition.data_outputs.map((o) =>
                      o.output_id === updated.output_id ? updated : o,
                    );
                    onChangeNoDelay({ ...condition, data_outputs: updatedOutputs });
                  }}
                  step={step}
                  conditionIndex={conditionIndex}
                  dataOutputIndex={dataOutputIndex}
                  isElseCondition={isElseCondition}
                  childSteps={condition.children}
                />
              ))}
              {condition.data_outputs.length === 0 && (
                <Text c="dimmed" fw={500} size={'xs'} mx={'auto'}>
                  No step outputs specified
                </Text>
              )}
            </Stack>
          </span>
        </>
      )}
    </Stack>
  );
};

export default ConditionFields;
